import { CallHistoryEntry, DirectoryEntry } from '../@types/connect.type';
import { HISTORY_LIMIT, STORAGE_NAMESPACE } from '../configs/connect';

// User-keyed localStorage helpers for the embedded softphone. Each key is
// suffixed with the agent's email so two agents sharing a workstation never
// see each other's history or directory. Email is normalised to lowercase.
//
// History and directory live in localStorage by product decision (no backend
// in this PR). All reads/writes are wrapped in try/catch — quota exceeded
// or private-mode failures must never crash the softphone.

const buildKey = (kind: 'history' | 'directory', userEmail: string): string =>
  `${STORAGE_NAMESPACE}_${kind}_${userEmail.trim().toLowerCase()}`;

const safeRead = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T) : fallback;
  } catch {
    return fallback;
  }
};

const safeWrite = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota exceeded / private mode — surface nothing, the in-memory state
    // is still correct for this session.
  }
};

export const readHistory = (userEmail: string): CallHistoryEntry[] =>
  safeRead<CallHistoryEntry[]>(buildKey('history', userEmail), []);

export const writeHistory = (userEmail: string, entries: CallHistoryEntry[]): void => {
  // Newest first, capped at HISTORY_LIMIT to bound localStorage growth.
  const trimmed = entries.slice(0, HISTORY_LIMIT);
  safeWrite(buildKey('history', userEmail), trimmed);
};

export const appendHistory = (userEmail: string, entry: CallHistoryEntry): CallHistoryEntry[] => {
  const next = [entry, ...readHistory(userEmail)].slice(0, HISTORY_LIMIT);
  writeHistory(userEmail, next);
  return next;
};

export const clearHistory = (userEmail: string): void => {
  writeHistory(userEmail, []);
};

export const readDirectory = (userEmail: string): DirectoryEntry[] =>
  safeRead<DirectoryEntry[]>(buildKey('directory', userEmail), []);

export const writeDirectory = (userEmail: string, entries: DirectoryEntry[]): void => {
  safeWrite(buildKey('directory', userEmail), entries);
};
